import { isAxiosError } from "axios";

import {
  EAnalyticsErrorKind,
  EAnalyticsEvent,
  EAnalyticsLatencyBucket,
  EAnalyticsShareKind,
  EAnalyticsSizeBucket,
  type IBuildAnalyticsPersonPropertiesProps,
  type ICaptureAnalyticsEventProps,
  type IGetAnalyticsErrorKindProps,
  type IGetEmailDomainProps,
  type IGetMeetingSharedEventProps,
  type TAnalyticsPersonProperties,
} from "./product-analytics.types";

// NOTE: Bucket boundaries in characters. The product question is "are people
// writing one-liners or pasting documents", which buckets answer while a raw
// length plus a timestamp would help re-identify a specific message.
const MESSAGE_LENGTH_SMALL_MAX = 50;
const MESSAGE_LENGTH_MEDIUM_MAX = 200;
const MESSAGE_LENGTH_LARGE_MAX = 1_000;
const MESSAGE_LENGTH_HUGE_MAX = 5_000;

// NOTE: Bucket boundaries in BYTES. Deliberately separate from the message
// boundaries above: reusing those would put every file above 5 KB in "huge",
// which is every file anyone ever uploads. These are sized for documents and
// meeting recordings instead.
const FILE_SIZE_SMALL_MAX = 100 * 1024;
const FILE_SIZE_MEDIUM_MAX = 1024 * 1024;
const FILE_SIZE_LARGE_MAX = 25 * 1024 * 1024;
const FILE_SIZE_HUGE_MAX = 250 * 1024 * 1024;

// NOTE: Bucket boundaries in milliseconds, chosen to line up with how the
// latency actually feels rather than with round powers of ten.
const LATENCY_1S = 1_000;
const LATENCY_3S = 3_000;
const LATENCY_10S = 10_000;
const LATENCY_30S = 30_000;

// NOTE: Callers pass the length, never the text itself — the signature is the
// enforcement point for the tracking plan's "send the shape, not the content"
// rule, so a message body can't reach an event property by accident.
export const getMessageLengthBucket = ({
  length,
}: {
  length: number;
}): EAnalyticsSizeBucket => {
  if (length < MESSAGE_LENGTH_SMALL_MAX) return EAnalyticsSizeBucket.TINY;
  if (length < MESSAGE_LENGTH_MEDIUM_MAX) return EAnalyticsSizeBucket.SMALL;
  if (length < MESSAGE_LENGTH_LARGE_MAX) return EAnalyticsSizeBucket.MEDIUM;
  if (length < MESSAGE_LENGTH_HUGE_MAX) return EAnalyticsSizeBucket.LARGE;
  return EAnalyticsSizeBucket.HUGE;
};

// NOTE: Callers pass the byte count, never the file or its name — the
// signature is the enforcement point for the tracking plan's "send the shape,
// not the content" rule.
export const getFileSizeBucket = ({
  sizeBytes,
}: {
  sizeBytes: number;
}): EAnalyticsSizeBucket => {
  if (sizeBytes < FILE_SIZE_SMALL_MAX) return EAnalyticsSizeBucket.TINY;
  if (sizeBytes < FILE_SIZE_MEDIUM_MAX) return EAnalyticsSizeBucket.SMALL;
  if (sizeBytes < FILE_SIZE_LARGE_MAX) return EAnalyticsSizeBucket.MEDIUM;
  if (sizeBytes < FILE_SIZE_HUGE_MAX) return EAnalyticsSizeBucket.LARGE;
  return EAnalyticsSizeBucket.HUGE;
};

export const getLatencyBucket = ({
  durationMs,
}: {
  durationMs: number;
}): EAnalyticsLatencyBucket => {
  if (durationMs < LATENCY_1S) return EAnalyticsLatencyBucket.UNDER_1S;
  if (durationMs < LATENCY_3S) return EAnalyticsLatencyBucket.UNDER_3S;
  if (durationMs < LATENCY_10S) return EAnalyticsLatencyBucket.UNDER_10S;
  if (durationMs < LATENCY_30S) return EAnalyticsLatencyBucket.UNDER_30S;
  return EAnalyticsLatencyBucket.OVER_30S;
};

// NOTE: The B2B segmentation key. Everything interesting about a self-serve
// account ("how many people from the same company signed up separately") is a
// question about the domain, not the mailbox.
// NSAI-7736 — no address means no domain, not a crash: identify() runs in an
// effect on every signed-in render of web and mobile, and a null here used to
// throw there and blank the whole app for any account without an email.
export const getEmailDomain = ({ email }: IGetEmailDomainProps): string => {
  if (!email) return "";

  const separatorIndex = email.lastIndexOf("@");
  if (separatorIndex === -1) return "";
  return email.slice(separatorIndex + 1).toLowerCase();
};

// NOTE: Single place where a user profile becomes PostHog person properties, so
// the PII rule in the tracking plan is enforced in one file rather than
// re-litigated at every identify() call site. NSAI-2433 — the email is read only
// to derive its domain; neither it nor the user's name leaves the product.
export const buildAnalyticsPersonProperties = ({
  email,
  isSsoUser,
  providers,
  subscriptionPlan,
  createdAt,
}: IBuildAnalyticsPersonPropertiesProps): TAnalyticsPersonProperties => {
  return {
    email_domain: getEmailDomain({ email }),
    is_sso_user: isSsoUser,
    provider: providers.join(","),
    subscription_plan: subscriptionPlan,
    created_at: createdAt,
  };
};

const HTTP_STATUS_ERROR_KINDS: ReadonlyMap<number, EAnalyticsErrorKind> =
  new Map([
    [400, EAnalyticsErrorKind.VALIDATION],
    [401, EAnalyticsErrorKind.UNAUTHORIZED],
    [403, EAnalyticsErrorKind.FORBIDDEN],
    [404, EAnalyticsErrorKind.NOT_FOUND],
    [409, EAnalyticsErrorKind.CONFLICT],
    [422, EAnalyticsErrorKind.VALIDATION],
    [429, EAnalyticsErrorKind.RATE_LIMITED],
  ]);

const HTTP_SERVER_ERROR_MIN = 500;

// NOTE: NSAI-4617 — the class of a failed request, from its status alone. The
// message is never read: a server or provider message can echo what the user
// typed, and the class is all a failure-rate chart needs.
export const getAnalyticsErrorKind = ({
  error,
}: IGetAnalyticsErrorKindProps): EAnalyticsErrorKind => {
  if (!isAxiosError(error)) return EAnalyticsErrorKind.UNKNOWN;

  // NOTE: No response at all means the request never completed — offline,
  // DNS, CORS or a timeout.
  if (!error.response) return EAnalyticsErrorKind.NETWORK;

  const { status } = error.response;
  if (status >= HTTP_SERVER_ERROR_MIN) return EAnalyticsErrorKind.SERVER;

  return HTTP_STATUS_ERROR_KINDS.get(status) ?? EAnalyticsErrorKind.UNKNOWN;
};

// NOTE: NSAI-6944 — the one decision both clients' bulk-share hooks make: one
// `meeting_shared` per invite action, and only when the server granted access to
// at least one recipient. A 2xx whose rows all failed shared nothing, so it
// returns null. Only `kind` travels — never the title or recipient emails.
export const getMeetingSharedEvent = ({
  results,
}: IGetMeetingSharedEventProps): ICaptureAnalyticsEventProps | null => {
  if (!results.some((result) => result.success)) return null;

  return {
    event: EAnalyticsEvent.MEETING_SHARED,
    properties: { kind: EAnalyticsShareKind.DIRECT },
  };
};
